import { useState, useEffect, useCallback } from "react";
import api from "@/services/api";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import {
  Search,
  FileSearch,
  RefreshCw,
  AlertCircle,
  Clock,
  XCircle,
  Timer,
  TrendingUp,
  CheckCircle2,
  AlertTriangle,
  GripVertical,
  Target,
  Euro,
  UserCircle,
  MapPin,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import LicitacionModal from "@/components/LicitacionModal";
import DateRangeFilter, { PRESETS_PLAZO } from "@/components/DateRangeFilter";

// Categoria config
const getCategoriaConfig = (categoria) => {
  const configs = {
    alta_prioridad: { color: "bg-green-500", textColor: "text-green-400", label: "Alta" },
    revisar: { color: "bg-yellow-500", textColor: "text-yellow-400", label: "Revisar" },
    baja_prioridad: { color: "bg-slate-500", textColor: "text-slate-400", label: "Baja" },
  };
  return configs[categoria] || configs.baja_prioridad;
};

// Estado config
const estadoConfig = {
  nueva: { icon: AlertCircle, color: "text-cyan-400", bg: "bg-cyan-500/20", label: "Promovida" },
  en_estudio: { icon: Search, color: "text-blue-400", bg: "bg-blue-500/20", label: "En Estudio" },
  viable: { icon: CheckCircle2, color: "text-green-400", bg: "bg-green-500/20", label: "Viable" },
  preparando_oferta: { icon: Clock, color: "text-purple-400", bg: "bg-purple-500/20", label: "Preparando" },
  oferta_presentada: { icon: TrendingUp, color: "text-indigo-400", bg: "bg-indigo-500/20", label: "Presentada" },
  en_evaluacion: { icon: Timer, color: "text-orange-400", bg: "bg-orange-500/20", label: "Evaluando" },
  adjudicada_srs: { icon: CheckCircle2, color: "text-emerald-400", bg: "bg-emerald-500/20", label: "Adjudicada" },
  no_adjudicada: { icon: XCircle, color: "text-red-400", bg: "bg-red-500/20", label: "No Adjudicada" },
  descartada: { icon: XCircle, color: "text-slate-400", bg: "bg-slate-500/20", label: "Descartada" },
  expirada: { icon: AlertTriangle, color: "text-slate-500", bg: "bg-slate-500/10", label: "Expirada" },
};

// Kanban columns — "nueva" con en_pipeline=true son las "Promovidas" por el analista
const KANBAN_COLUMNS = [
  { id: "nueva", label: "Promovida", borderColor: "#06b6d4", bgClass: "bg-cyan-500/10" },
  { id: "en_estudio", label: "En Estudio", borderColor: "#3b82f6", bgClass: "bg-blue-500/10" },
  { id: "viable", label: "Viable", borderColor: "#22c55e", bgClass: "bg-green-500/10" },
  { id: "preparando_oferta", label: "Preparando", borderColor: "#a855f7", bgClass: "bg-purple-500/10" },
  { id: "oferta_presentada", label: "Presentada", borderColor: "#6366f1", bgClass: "bg-indigo-500/10" },
  { id: "en_evaluacion", label: "Evaluando", borderColor: "#f97316", bgClass: "bg-orange-500/10" },
  { id: "adjudicada_srs", label: "Adjudicada", borderColor: "#10b981", bgClass: "bg-emerald-500/10" },
];
const TERMINAL_STATES = ["no_adjudicada", "descartada", "expirada"];

// Helpers
const getDiasRestantes = (fechaLimite) => {
  if (!fechaLimite) return null;
  const ahora = new Date();
  const limite = new Date(fechaLimite);
  return Math.ceil((limite - ahora) / (1000 * 60 * 60 * 24));
};

const getDiasColor = (dias) => {
  if (dias === null) return "text-slate-500";
  if (dias <= 0) return "text-red-500";
  if (dias <= 7) return "text-red-400";
  if (dias <= 15) return "text-orange-400";
  if (dias <= 30) return "text-yellow-400";
  return "text-green-400";
};

const formatPilarName = (pilar) => {
  if (!pilar) return pilar;
  return pilar
    .replace(/_/g, " ")
    .split(" ")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
};

const formatCurrency = (value) => {
  if (!value) return "-";
  return new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

export default function PipelineLicitaciones({ user }) {
  const [licitaciones, setLicitaciones] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  // Filters
  const [search, setSearch] = useState("");
  const [categoriaFilter, setCategoriaFilter] = useState("todas");
  const [pilarFilter, setPilarFilter] = useState("todos");
  const [regionFilter, setRegionFilter] = useState("todos");
  // v4.4.33 — filtro fecha (plazo presentación)
  const [fechaRango, setFechaRango] = useState({ desde: null, hasta: null });

  // Modal
  const [selectedLicitacion, setSelectedLicitacion] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const fetchLicitaciones = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (categoriaFilter && categoriaFilter !== "todas") params.append("categoria", categoriaFilter);
      if (pilarFilter && pilarFilter !== "todos") params.append("pilar", pilarFilter);
      if (regionFilter && regionFilter !== "todos") params.append("lugar_ejecucion", regionFilter);
      if (search) params.append("busqueda", search);
      // v4.4.33 — rango fechas (plazo presentación)
      if (fechaRango.desde) params.append("fecha_desde", fechaRango.desde);
      if (fechaRango.hasta) params.append("fecha_hasta", fechaRango.hasta);
      params.append("orden", "score");
      params.append("limit", "500");

      const response = await api.get(`/licitaciones?${params.toString()}`);
      // Pipeline: show promoted "nueva" (en_pipeline=true) + all non-nueva states
      const items = (response.data.items || []).filter(
        l => l.en_pipeline === true || l.estado !== "nueva"
      );
      setLicitaciones(items);
    } catch (error) {
      toast.error("Error al cargar licitaciones");
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [categoriaFilter, pilarFilter, regionFilter, search, fechaRango]);

  const fetchStats = async () => {
    try {
      const response = await api.get('/licitaciones/stats');
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
      toast.error("Error cargando estadísticas");
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    fetchLicitaciones();
  }, [fetchLicitaciones]);

  const openModal = (lic) => {
    setSelectedLicitacion(lic);
    setModalOpen(true);
  };

  const closeModal = () => {
    setSelectedLicitacion(null);
    setModalOpen(false);
    fetchLicitaciones();
    fetchStats();
  };

  // Drag & drop
  const handleDragEnd = async (result) => {
    const { destination, source, draggableId } = result;
    if (!destination) return;
    if (destination.droppableId === source.droppableId) return;

    const nextEstado = destination.droppableId;
    const movedLic = licitaciones.find((l) => l._id === draggableId);

    // Optimistic update
    setLicitaciones((prev) =>
      prev.map((l) => (l._id === draggableId ? { ...l, estado: nextEstado } : l))
    );

    try {
      await api.put(
        `/licitaciones/${draggableId}`,
        { estado: nextEstado }
      );
      const label = estadoConfig[nextEstado]?.label || nextEstado;
      toast.success(`${movedLic?.expediente?.slice(-10) || ""} → ${label}`);
    } catch (error) {
      // Revert
      setLicitaciones((prev) =>
        prev.map((l) =>
          l._id === draggableId ? { ...l, estado: source.droppableId } : l
        )
      );
      toast.error("Error al cambiar estado");
    }
  };

  const clearFilters = () => {
    setSearch("");
    setCategoriaFilter("todas");
    setPilarFilter("todos");
    setRegionFilter("todos");
    setFechaRango({ desde: null, hasta: null });
  };

  const hasFilters = search || categoriaFilter !== "todas" || pilarFilter !== "todos" || regionFilter !== "todos" || fechaRango.desde || fechaRango.hasta;

  // Column totals
  const getColumnTotal = (colId) => {
    return licitaciones
      .filter((l) => l.estado === colId)
      .reduce((sum, l) => sum + (l.presupuesto_base || 0), 0);
  };

  return (
    <div data-testid="pipeline-licitaciones-page" className="h-full flex flex-col">
      {/* Header */}
      <div className="mb-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold font-display theme-text flex items-center gap-2">
              <FileSearch className="w-7 h-7 text-green-400" />
              Pipeline Licitaciones
            </h1>
            <p className="theme-text-secondary text-sm">
              Arrastra licitaciones entre etapas del proceso
            </p>
          </div>

          {/* Compact filters */}
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <Search className="absolute left-2.5 top-2 h-4 w-4 text-slate-500" />
              <Input
                placeholder="Buscar..."
                className="pl-8 bg-slate-950 border-slate-800 focus:border-green-500/50 h-9 w-48 text-sm"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <Select value={categoriaFilter} onValueChange={setCategoriaFilter}>
              <SelectTrigger className="bg-slate-950 border-slate-800 h-9 w-36 text-sm">
                <SelectValue placeholder="Categoría" />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-800">
                <SelectItem value="todas">Todas</SelectItem>
                <SelectItem value="alta_prioridad">Alta Prioridad</SelectItem>
                <SelectItem value="revisar">Revisar</SelectItem>
                <SelectItem value="baja_prioridad">Baja Prioridad</SelectItem>
              </SelectContent>
            </Select>

            <Select value={pilarFilter} onValueChange={setPilarFilter}>
              <SelectTrigger className="bg-slate-950 border-slate-800 h-9 w-44 text-sm">
                <SelectValue placeholder="Pilar" />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-800">
                <SelectItem value="todos">Todos pilares</SelectItem>
                {stats?.por_pilar &&
                  Object.entries(stats.por_pilar)
                    .filter(([key]) => key !== "NO_CLASIFICADO")
                    .sort(([, a], [, b]) => b - a)
                    .map(([pilar, count]) => (
                      <SelectItem key={pilar} value={pilar}>
                        {formatPilarName(pilar)} ({count})
                      </SelectItem>
                    ))}
              </SelectContent>
            </Select>

            <Select value={regionFilter} onValueChange={setRegionFilter}>
              <SelectTrigger className="bg-slate-950 border-slate-800 h-9 w-44 text-sm">
                <MapPin className="w-3.5 h-3.5 mr-1 text-slate-500" />
                <SelectValue placeholder="Región" />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-800 max-h-60">
                <SelectItem value="todos">Todas regiones</SelectItem>
                {stats?.por_region &&
                  Object.entries(stats.por_region)
                    .sort(([a], [b]) => a.localeCompare(b))
                    .map(([region, count]) => (
                      <SelectItem key={region} value={region}>
                        {region} ({count})
                      </SelectItem>
                    ))}
              </SelectContent>
            </Select>

            {/* v4.4.33 — Filtro fecha (plazo) */}
            <DateRangeFilter
              value={fechaRango}
              onChange={setFechaRango}
              label="Plazo"
              compact
              presets={PRESETS_PLAZO}
            />

            {hasFilters && (
              <Button
                variant="ghost"
                size="icon"
                onClick={clearFilters}
                className="h-9 w-9 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded-full"
                title="Limpiar filtros"
              >
                <XCircle className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Kanban Board */}
      <DragDropContext onDragEnd={handleDragEnd}>
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <RefreshCw className="w-6 h-6 animate-spin text-green-400" />
            <p className="ml-3 theme-text-muted">Cargando pipeline...</p>
          </div>
        ) : (
          <div className="flex gap-3 overflow-x-auto pb-4 flex-1" style={{ minHeight: "calc(100vh - 220px)" }}>
            {/* Active columns */}
            {KANBAN_COLUMNS.map((col) => {
              const colLics = licitaciones.filter((l) => l.estado === col.id);
              const colTotal = getColumnTotal(col.id);
              const EstadoIcon = estadoConfig[col.id]?.icon;

              return (
                <div key={col.id} className="flex-shrink-0 w-72 flex flex-col">
                  {/* Column Header */}
                  <div
                    className={`rounded-t-xl p-3 ${col.bgClass}`}
                    style={{ borderTop: `3px solid ${col.borderColor}` }}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        {EstadoIcon && <EstadoIcon className="w-4 h-4 theme-text" />}
                        <span className="font-semibold theme-text text-sm">{col.label}</span>
                      </div>
                      <Badge variant="secondary" className="theme-bg-secondary theme-text text-xs">
                        {colLics.length}
                      </Badge>
                    </div>
                    <div className="text-xs" style={{ color: "var(--theme-currency, #4ade80)" }}>
                      {formatCurrency(colTotal)}
                    </div>
                  </div>

                  {/* Droppable Area */}
                  <Droppable droppableId={col.id}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className={`flex-1 bg-slate-950/30 rounded-b-xl p-2 border border-slate-800 border-t-0 transition-colors ${
                          snapshot.isDraggingOver ? "ring-2 ring-green-500/30 bg-green-500/5" : ""
                        }`}
                        style={{ minHeight: "200px" }}
                      >
                        {colLics.length === 0 && !snapshot.isDraggingOver && (
                          <div className="text-center py-8 text-slate-600 text-xs">
                            Sin licitaciones
                          </div>
                        )}
                        {colLics.map((lic, idx) => {
                          const catConfig = getCategoriaConfig(lic.categoria);
                          const dias = getDiasRestantes(lic.fecha_limite_presentacion);

                          return (
                            <Draggable key={lic._id} draggableId={lic._id} index={idx}>
                              {(prov, snap) => (
                                <div
                                  ref={prov.innerRef}
                                  {...prov.draggableProps}
                                  className="mb-2"
                                >
                                  <Card
                                    className={`p-3 cursor-pointer theme-bg-secondary border-slate-800 hover:border-green-500/30 transition-all group ${
                                      snap.isDragging ? "shadow-xl ring-2 ring-green-500/30" : ""
                                    }`}
                                    onClick={() => openModal(lic)}
                                  >
                                    {/* Drag handle + Score + Expediente */}
                                    <div className="flex items-center gap-1.5 mb-1.5">
                                      <div
                                        {...prov.dragHandleProps}
                                        className="theme-text-muted opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing"
                                      >
                                        <GripVertical className="w-3 h-3" />
                                      </div>
                                      <div className={`w-1.5 h-5 rounded-full ${catConfig.color}`} />
                                      <span className={`font-bold text-sm ${catConfig.textColor}`}>
                                        {lic.score_total}
                                      </span>
                                      <span className="font-mono text-[10px] text-slate-500 ml-auto">
                                        {lic.expediente?.slice(-10)}
                                      </span>
                                    </div>

                                    {/* Objeto truncated */}
                                    <p className="text-xs theme-text line-clamp-2 mb-2 leading-relaxed">
                                      {lic.objeto_contrato}
                                    </p>

                                    {/* Organo */}
                                    <p className="text-[10px] text-slate-500 truncate mb-2">
                                      {lic.organo_contratacion}
                                    </p>

                                    {/* Bottom row */}
                                    <div className="flex items-center justify-between text-[11px]">
                                      <span className="text-green-400 font-semibold">
                                        {formatCurrency(lic.presupuesto_base)}
                                      </span>
                                      {dias !== null && (
                                        <span className={`font-bold ${getDiasColor(dias)}`}>
                                          {dias <= 0 ? "Vencida" : `${dias}d`}
                                        </span>
                                      )}
                                    </div>

                                    {/* Pilar + Responsable row */}
                                    <div className="flex items-center justify-between mt-1.5">
                                      {lic.pilar_principal &&
                                        lic.pilar_principal !== "NO_CLASIFICADO" ? (
                                          <Badge
                                            variant="outline"
                                            className="border-green-500/30 text-green-400 text-[10px] py-0"
                                          >
                                            {formatPilarName(lic.pilar_principal.split(" / ")[0])}
                                          </Badge>
                                        ) : (
                                          <span />
                                        )}
                                      {lic.responsable && (
                                        <div className="flex items-center gap-1">
                                          <UserCircle className="w-3 h-3" style={{ color: "var(--theme-accent)" }} />
                                          <span className="text-[10px]" style={{ color: "var(--theme-accent)" }}>
                                            {lic.responsable.split(" ")[0]}
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  </Card>
                                </div>
                              )}
                            </Draggable>
                          );
                        })}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </div>
              );
            })}

            {/* Terminal states column */}
            <div className="flex-shrink-0 w-56 flex flex-col">
              <div className="rounded-xl p-3 bg-slate-800/30 border border-slate-800">
                <p className="text-[10px] text-slate-500 uppercase tracking-widest font-bold mb-3">
                  Finalizadas
                </p>
                {TERMINAL_STATES.map((ts) => {
                  const cfg = estadoConfig[ts];
                  const tsLics = licitaciones.filter((l) => l.estado === ts);
                  const Icon = cfg?.icon;
                  return (
                    <div
                      key={ts}
                      className="flex items-center justify-between py-1.5"
                    >
                      <div className="flex items-center gap-2">
                        {Icon && <Icon className={`w-3.5 h-3.5 ${cfg.color}`} />}
                        <span className={`text-xs ${cfg.color}`}>{cfg.label}</span>
                      </div>
                      <Badge variant="outline" className="text-[10px] border-slate-700 text-slate-400">
                        {tsLics.length}
                      </Badge>
                    </div>
                  );
                })}
                {licitaciones.filter((l) => TERMINAL_STATES.includes(l.estado)).length === 0 && (
                  <p className="text-xs text-slate-600 text-center py-2">Ninguna</p>
                )}

                {/* Summary */}
                <div className="mt-3 pt-3 border-t border-slate-700">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-slate-400">Total pipeline</span>
                    <span className="font-bold text-green-400">{licitaciones.length}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs mt-1">
                    <span className="text-slate-400">Activas</span>
                    <span className="font-bold theme-text">
                      {licitaciones.filter((l) => !TERMINAL_STATES.includes(l.estado)).length}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </DragDropContext>

      {/* Modal */}
      <LicitacionModal
        open={modalOpen}
        onClose={closeModal}
        licitacion={selectedLicitacion}
        user={user}
      />
    </div>
  );
}
